import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, ChevronLeft, ChevronRight, X, AlertTriangle, Plus } from 'lucide-react';
import appointmentService from '../services/appointmentService';
import { getFranchiseeIdForDataLoading } from '../App/services/appHelpers';
import { useToast } from './Toast';
import useTechnicianFilter from '../hooks/useTechnicianFilter';
import TechnicianFilter from './TechnicianFilter';
import useMobileHeaderActions from '../hooks/useMobileHeaderActions';
import MobileFAB from './MobileFAB';
import AppointmentTimeline from './AppointmentTimeline';

const TYPE_OPTIONS = [
  { value: 'first_visit', label: 'First Visit' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'treatment', label: 'Treatment' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'final_visit', label: 'Final Visit' },
];

const DATE_PRESETS = [
  { value: 'all', label: 'All Dates' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

const getDateRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const fmt = (d) => d.toISOString().split('T')[0];

  switch (preset) {
    case 'today':
      return { date_from: fmt(today), date_to: fmt(today) };
    case 'week': {
      const start = new Date(today);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { date_from: fmt(start), date_to: fmt(end) };
    }
    case 'last7': {
      const start = new Date(today);
      start.setDate(start.getDate() - 7);
      return { date_from: fmt(start), date_to: fmt(today) };
    }
    case 'last30': {
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      return { date_from: fmt(start), date_to: fmt(today) };
    }
    case 'month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { date_from: fmt(start), date_to: fmt(end) };
    }
    default:
      return {};
  }
};

const AppointmentList = ({ user, userProfile, onNavigate, initialFilter, onAddJob, onAddContract }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(initialFilter || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [datePreset, setDatePreset] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const toast = useToast();
  const techFilter = useTechnicianFilter(userProfile);

  useMobileHeaderActions(
    techFilter.showFilter ? <TechnicianFilter {...techFilter} compact /> : null,
    [techFilter.showFilter, techFilter.selectedTechnician, techFilter.techGroups]
  );

  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingAppointment, setCancellingAppointment] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSaving, setCancelSaving] = useState(false);

  // Reschedule modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [reschedulingAppointment, setReschedulingAppointment] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('09:00');
  const [rescheduleConfirmed, setRescheduleConfirmed] = useState(false);
  const [rescheduleNotes, setRescheduleNotes] = useState('');
  const [rescheduleSaving, setRescheduleSaving] = useState(false);

  const currentUser = userProfile || user;
  const defaultFranchiseeId = getFranchiseeIdForDataLoading(currentUser);
  const franchiseeId = techFilter.showFilter ? techFilter.getFilteredFranchiseeId() : defaultFranchiseeId;
  const selectedBranchId = techFilter.getSelectedBranchId();

  // Fetch status counts
  const fetchCounts = useCallback(async () => {
    if (!franchiseeId) return;
    try {
      const counts = await appointmentService.getAppointmentCounts(franchiseeId);
      setStatusCounts(counts);
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  }, [franchiseeId]);

  // Fetch appointments
  const fetchAppointments = useCallback(async () => {
    if (!franchiseeId) return;
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 25,
        sortBy: 'scheduled_date',
        sortOrder: statusFilter === 'overdue' ? 'ASC' : 'DESC',
      };

      if (statusFilter === 'overdue') {
        params.overdue = true;
      } else if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (typeFilter !== 'all') {
        params.appointment_type = typeFilter;
      }

      // Date range
      if (datePreset === 'custom') {
        if (customDateFrom) params.date_from = customDateFrom;
        if (customDateTo) params.date_to = customDateTo;
      } else if (datePreset !== 'all') {
        const range = getDateRange(datePreset);
        if (range.date_from) params.date_from = range.date_from;
        if (range.date_to) params.date_to = range.date_to;
      }

      // Technician filter
      if (selectedBranchId) {
        params.branch_id = selectedBranchId;
      }

      const response = await appointmentService.getAppointments(franchiseeId, params);
      setAppointments(response.appointments || []);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalItems(response.pagination?.totalItems || 0);
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [franchiseeId, currentPage, statusFilter, typeFilter, datePreset, customDateFrom, customDateTo, selectedBranchId]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);
  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [statusFilter, typeFilter, datePreset, customDateFrom, customDateTo, techFilter.selectedTechnician]);

  // Filter by search term (client-side on current page)
  const filtered = searchTerm
    ? appointments.filter(a =>
        [a.job_title, a.customer_name, a.technician_name, a.treatment_address_line1, a.treatment_address_postcode]
          .filter(Boolean).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : appointments;

  // Status change handler
  const handleStatusChange = async (appointment, newStatus) => {

    if (newStatus === 'cancelled') {
      setCancellingAppointment(appointment);
      setCancelReason('');
      setShowCancelModal(true);
      return;
    }

    if (newStatus === 'rescheduled') {
      setReschedulingAppointment(appointment);
      setRescheduleDate(new Date().toISOString().split('T')[0]);
      setRescheduleTime('09:00');
      setRescheduleConfirmed(false);
      setRescheduleNotes('');
      setShowRescheduleModal(true);
      return;
    }

    const prevStatus = appointment.status;
    // Optimistic update: remove from list if it no longer matches the active filter
    const shouldRemove =
      (statusFilter === 'overdue' && ['completed', 'cancelled'].includes(newStatus)) ||
      (statusFilter !== 'all' && statusFilter !== 'overdue' && newStatus !== statusFilter);

    if (shouldRemove) {
      setAppointments(prev => prev.filter(a => a.id !== appointment.id));
      setTotalItems(prev => Math.max(0, prev - 1));
    } else {
      setAppointments(prev => prev.map(a => a.id === appointment.id ? { ...a, status: newStatus } : a));
    }

    try {
      const aptFranchiseeId = appointment.franchisee_id || franchiseeId;
      await appointmentService.updateAppointment(aptFranchiseeId, appointment.id, { status: newStatus });
      const label = newStatus.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      toast.success(`Appointment status updated to ${label}`);
      fetchCounts();
    } catch (error) {
      console.error('Error updating status:', error);
      // Revert: re-fetch the full list to restore correct state
      fetchAppointments();
      toast.error('Failed to update appointment status');
    }
  };

  // Cancel confirmation
  const confirmCancel = async () => {
    if (!cancellingAppointment || cancelReason.trim().length < 3) return;
    setCancelSaving(true);

    try {
      const aptFranchiseeId = cancellingAppointment.franchisee_id || franchiseeId;
      const existingNotes = cancellingAppointment.notes || '';
      const updatedNotes = existingNotes
        ? `${existingNotes}\nCancelled: ${cancelReason.trim()}`
        : `Cancelled: ${cancelReason.trim()}`;

      await appointmentService.updateAppointment(aptFranchiseeId, cancellingAppointment.id, {
        status: 'cancelled',
        notes: updatedNotes,
      });

      // Remove from list if it no longer matches the active filter
      const shouldRemove = statusFilter === 'overdue' || (statusFilter !== 'all' && statusFilter !== 'cancelled');
      if (shouldRemove) {
        setAppointments(prev => prev.filter(a => a.id !== cancellingAppointment.id));
        setTotalItems(prev => Math.max(0, prev - 1));
      } else {
        setAppointments(prev => prev.map(a =>
          a.id === cancellingAppointment.id ? { ...a, status: 'cancelled', notes: updatedNotes } : a
        ));
      }
      toast.success('Appointment cancelled');
      fetchCounts();
      setShowCancelModal(false);
      setCancellingAppointment(null);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Failed to cancel appointment');
    } finally {
      setCancelSaving(false);
    }
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setCancellingAppointment(null);
    setCancelReason('');
  };

  // Reschedule confirmation
  const confirmReschedule = async () => {
    if (!reschedulingAppointment || !rescheduleDate || !rescheduleTime) return;
    setRescheduleSaving(true);

    try {
      const aptFranchiseeId = reschedulingAppointment.franchisee_id || franchiseeId;
      const newStatus = rescheduleConfirmed ? 'confirmed' : 'scheduled';
      const newDateTime = `${rescheduleDate} ${rescheduleTime}:00`;

      // Calculate end date based on existing duration
      const duration = reschedulingAppointment.estimated_duration || 60;
      const startDate = new Date(`${rescheduleDate}T${rescheduleTime}:00`);
      const endDate = new Date(startDate.getTime() + duration * 60000);
      const newEndDateTime = endDate.toISOString().slice(0, 19).replace('T', ' ');

      const updateData = {
        status: newStatus,
        scheduled_date: newDateTime,
        scheduled_end_date: newEndDateTime,
      };

      // Append notes if provided
      if (rescheduleNotes.trim()) {
        const existingNotes = reschedulingAppointment.notes || '';
        const oldDate = new Date(reschedulingAppointment.scheduled_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        const noteEntry = `Rescheduled from ${oldDate}: ${rescheduleNotes.trim()}`;
        updateData.notes = existingNotes ? `${existingNotes}\n${noteEntry}` : noteEntry;
      }

      await appointmentService.updateAppointment(aptFranchiseeId, reschedulingAppointment.id, updateData);

      // Remove from list if it no longer matches the active filter
      const shouldRemove =
        statusFilter === 'overdue' ||
        (statusFilter !== 'all' && statusFilter !== newStatus);
      if (shouldRemove) {
        setAppointments(prev => prev.filter(a => a.id !== reschedulingAppointment.id));
        setTotalItems(prev => Math.max(0, prev - 1));
      } else {
        setAppointments(prev => prev.map(a =>
          a.id === reschedulingAppointment.id
            ? { ...a, status: newStatus, scheduled_date: newDateTime, scheduled_end_date: newEndDateTime }
            : a
        ));
      }

      toast.success(`Rescheduled to ${new Date(`${rescheduleDate}T${rescheduleTime}`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} at ${rescheduleTime}`);
      fetchCounts();
      setShowRescheduleModal(false);
      setReschedulingAppointment(null);
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      toast.error('Failed to reschedule appointment');
    } finally {
      setRescheduleSaving(false);
    }
  };

  const closeRescheduleModal = () => {
    setShowRescheduleModal(false);
    setReschedulingAppointment(null);
  };

  const activeFilterCount = [typeFilter !== 'all', datePreset !== 'all'].filter(Boolean).length;

  const statusPills = [
    { value: 'all', label: 'All', count: statusCounts.total },
    { value: 'overdue', label: 'Overdue', count: statusCounts.overdue, amber: true },
    { value: 'scheduled', label: 'Scheduled', count: statusCounts.scheduled },
    { value: 'confirmed', label: 'Confirmed', count: statusCounts.confirmed },
    { value: 'completed', label: 'Completed', count: statusCounts.completed },
    { value: 'cancelled', label: 'Cancelled', count: statusCounts.cancelled },
    { value: 'rescheduled', label: 'Rescheduled', count: statusCounts.rescheduled },
  ];

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <MobileFAB onAddJob={onAddJob} onAddContract={onAddContract} />
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-2 sm:px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Appointments</h1>
            <p className="text-sm text-gray-500 hidden sm:block">Manage and update appointment statuses</p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            {onAddJob && (
              <button onClick={onAddJob} className="btn btn-md btn-secondary">
                <Plus size={16} />
                <span>Add Job</span>
              </button>
            )}
            {onAddContract && (
              <button onClick={onAddContract} className="btn btn-md btn-secondary">
                <Plus size={16} />
                <span>Add Contract</span>
              </button>
            )}
            <TechnicianFilter {...techFilter} />
          </div>
        </div>

        {/* Search + Filter Toggle */}
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search customer, job, address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pest-blue focus:border-transparent"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={14} />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
              showFilters || activeFilterCount > 0
                ? 'bg-pest-blue text-white border-pest-blue'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter size={14} />
            <span className="hidden sm:inline">Filters</span>
            {activeFilterCount > 0 && (
              <span className="bg-white text-pest-blue text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Status Filter Pills */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
          {statusPills.map(pill => (
            <button
              key={pill.value}
              onClick={() => setStatusFilter(pill.value)}
              className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                statusFilter === pill.value
                  ? pill.amber
                    ? 'bg-amber-500 text-white'
                    : 'bg-pest-blue text-white'
                  : pill.amber
                    ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {pill.amber && <span className="mr-0.5">&#9888;</span>}
              {pill.label}
              {pill.count !== undefined && (
                <span className={`ml-1 ${statusFilter === pill.value ? 'opacity-80' : 'opacity-60'}`}>
                  {pill.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Expanded Filters Panel */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Appointment Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-pest-blue"
              >
                <option value="all">All Types</option>
                {TYPE_OPTIONS.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date Range</label>
              <select
                value={datePreset}
                onChange={(e) => setDatePreset(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-pest-blue"
              >
                {DATE_PRESETS.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
            {datePreset === 'custom' && (
              <div className="flex gap-2 sm:col-span-1">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                  <input type="date" value={customDateFrom} onChange={(e) => setCustomDateFrom(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-pest-blue" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                  <input type="date" value={customDateTo} onChange={(e) => setCustomDateTo(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-pest-blue" />
                </div>
              </div>
            )}
            {activeFilterCount > 0 && (
              <div className="sm:col-span-3">
                <button
                  onClick={() => { setTypeFilter('all'); setDatePreset('all'); setCustomDateFrom(''); setCustomDateTo(''); }}
                  className="text-xs text-pest-blue hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Appointment Cards */}
      <div className="flex-1 overflow-y-auto px-2 sm:px-6 py-4">
        {loading ? (
          <div className="text-gray-500 text-center py-12">Loading appointments...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No appointments found</p>
            {statusFilter !== 'all' && (
              <button onClick={() => setStatusFilter('all')} className="text-sm text-pest-blue hover:underline mt-2">
                Show all appointments
              </button>
            )}
          </div>
        ) : (
          <AppointmentTimeline
            appointments={filtered}
            onStatusChange={handleStatusChange}
            onClick={(apt) => {
              if (apt.job_id) {
                onNavigate('job-details', {
                  id: apt.job_id,
                  title: apt.job_title,
                  customer_id: apt.customer_id,
                  customer_name: apt.customer_name,
                });
              }
            }}
          />
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Cancel Reason Modal */}
      {showCancelModal && cancellingAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) { setShowCancelModal(false); setCancellingAppointment(null); } }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Cancel Appointment</h3>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-3">
                <strong>{cancellingAppointment.job_title}</strong>
                {cancellingAppointment.customer_name && ` — ${cancellingAppointment.customer_name}`}
                <br />
                <span className="text-xs text-gray-400">
                  {formatDate(cancellingAppointment.scheduled_date)} at {formatTime(cancellingAppointment.scheduled_date)}
                </span>
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for cancellation <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancelling..."
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pest-blue"
                rows={3}
                autoFocus
              />
              {cancelReason.length > 0 && cancelReason.trim().length < 3 && (
                <p className="text-xs text-red-500 mt-1">Please provide at least 3 characters</p>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={closeCancelModal}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
              >
                Go Back
              </button>
              <button
                onClick={confirmCancel}
                disabled={cancelReason.trim().length < 3 || cancelSaving}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
              >
                {cancelSaving ? 'Cancelling...' : 'Cancel Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && reschedulingAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) { setShowRescheduleModal(false); setReschedulingAppointment(null); } }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Reschedule Appointment</h3>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">
                <strong>{reschedulingAppointment.job_title}</strong>
                {reschedulingAppointment.customer_name && ` — ${reschedulingAppointment.customer_name}`}
                <br />
                <span className="text-xs text-gray-400">
                  Currently: {formatDate(reschedulingAppointment.scheduled_date)} at {formatTime(reschedulingAppointment.scheduled_date)}
                </span>
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Date</label>
                  <input
                    type="date"
                    value={rescheduleDate}
                    onChange={(e) => setRescheduleDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pest-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Time</label>
                  <input
                    type="time"
                    value={rescheduleTime}
                    onChange={(e) => setRescheduleTime(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pest-blue"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rescheduleConfirmed}
                  onChange={(e) => setRescheduleConfirmed(e.target.checked)}
                  className="w-4 h-4 text-pest-blue border-gray-300 rounded focus:ring-pest-blue"
                />
                <span className="text-sm text-gray-700">Confirmed with customer?</span>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea
                  value={rescheduleNotes}
                  onChange={(e) => setRescheduleNotes(e.target.value)}
                  placeholder="Reason for rescheduling..."
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pest-blue"
                  rows={2}
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={closeRescheduleModal}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmReschedule}
                disabled={!rescheduleDate || !rescheduleTime || rescheduleSaving}
                className="px-4 py-2 text-sm bg-pest-blue text-white rounded-lg hover:bg-pest-blue/90 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
              >
                {rescheduleSaving ? 'Rescheduling...' : 'Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AppointmentList;
